/**
 * `/terms` — the terms of use.
 *
 * FOUNDER-DIRECTED, recorded in `docs/DEVIATIONS.md` **W3**.
 * `docs/marketing/app-store-listing.md` §URLs enumerates three URLs and states "All three
 * are built and live in `apps/web`"; this is a fourth page that document does not name, so
 * it is an entry in the deviations ledger rather than something presented as authorised.
 * The requirement it answers is real — product-concept §12 sells Carn Plus as an
 * auto-renewing subscription, and App Store Review 3.1.2 wants a functional Terms of Use
 * link wherever such a subscription is sold — but that reasoning belongs in the ledger,
 * not in a comment claiming a section that does not exist.
 *
 * The section order answers the questions in the order a reader has them, which is not the
 * order a template would use. Who is on the other side of this; is it pretending to be
 * medicine; whose data is it; then money, then sharing, then the machinery. A generated
 * agreement opens with definitions and a licence grant; nobody has ever wanted either
 * first.
 *
 * FOUR OF W3's FIVE MISSING LEGAL DECISIONS HAVE SINCE LANDED, all as founder rulings:
 *   - Governing law: RULINGS R133. England and Wales, and its courts —
 *     `siteTerms.law.*`, near the end, where a dispute clause conventionally sits.
 *   - The liability cap: RULINGS R138. The last twelve months of Carn Plus fees paid —
 *     `siteTerms.noWarranty.liability`, beside the consumer-law override that was
 *     already on the page.
 *   - The age clauses: RULINGS R140. 13+ to use, 18+ or parental consent to subscribe
 *     — `siteTerms.age.*`, between Accounts and Carn Plus.
 *
 * WHAT IS STILL NOT HERE, AND WHY:
 *   - No arbitration clause, no class-action waiver. RULINGS R139 CONFIRMS the absence
 *     as a decision, not a gap: disputes go to the courts of England and Wales per
 *     R133, and an arbitration clause unenforceable against the actual UK/EU audience
 *     is decoration that reads as hostility on a page whose voice is deliberately
 *     plain. Do not add either — a violation here is a bug, not a tradeoff.
 *
 * Every clause states shipped behaviour. The `siteTerms.*` block header in
 * `packages/copy/src/catalogue.ts` lists the file behind each one and is the thing to read
 * before changing any sentence on this page.
 */
import { PageFrame, Section } from './PageFrame';
import { useCopy } from '../theme/CopyProvider';
import { useTheme } from '../theme/ThemeProvider';
import { text } from './prose';

/**
 * The date these terms last changed, and the ONLY place it is written.
 *
 * `siteTerms.changes.body` promises that a change here changes the date at the top, which
 * is a promise about this constant. Update it in the same commit as any `siteTerms.*`
 * string, or the page tells the reader something false about itself. `PrivacyPage.tsx`
 * carries its own, deliberately: the two documents change on their own schedules, and one
 * shared constant would silently redate whichever page did not change.
 */
const LAST_UPDATED = '31 July 2026';

export function TermsPage() {
  const theme = useTheme();
  const { t } = useCopy();
  const c = theme.colours;
  const s = theme.space;

  return (
    <PageFrame current="/terms" titleKey="siteTerms.title">
      <section style={{ display: 'flex', flexDirection: 'column', gap: s.sm }}>
        <p style={{ ...text(theme.type.body), color: c.textSecondary, margin: 0 }}>
          {t('siteTerms.intro')}
        </p>
        <p style={{ ...text(theme.type.quiet), color: c.textTertiary, margin: 0 }}>
          {t('siteTerms.updated', { when: LAST_UPDATED })}
        </p>
      </section>

      <Section
        titleKey="siteTerms.who.title"
        bodyKeys={['siteTerms.who.body', 'siteTerms.who.contact']}
      />

      <Section
        titleKey="siteTerms.notMedical.title"
        bodyKeys={['siteTerms.notMedical.body', 'siteTerms.notMedical.advice']}
      />

      <Section
        titleKey="siteTerms.yourContent.title"
        bodyKeys={['siteTerms.yourContent.body', 'siteTerms.yourContent.device']}
      />

      <Section
        titleKey="siteTerms.account.title"
        bodyKeys={[
          'siteTerms.account.optional',
          'siteTerms.account.keep',
          'siteTerms.account.one',
        ]}
      />

      {/* RULINGS R140: 13+ to use, 18+ or parental consent to subscribe. Sits between
          Accounts and Carn Plus because the second threshold is about the section that
          follows it. No age gate — a gate is an interrogation at first run, which
          product-concept §11 exists to avoid; a stated term is the proportionate form. */}
      <Section
        titleKey="siteTerms.age.title"
        bodyKeys={['siteTerms.age.use', 'siteTerms.age.subscribe']}
      />

      <Section
        titleKey="siteTerms.plus.title"
        bodyKeys={[
          'siteTerms.plus.free',
          'siteTerms.plus.billing',
          'siteTerms.plus.cancel',
          'siteTerms.plus.refunds',
        ]}
      />

      {/* Apple's own minimum terms for a developer-supplied agreement: the store's terms
          also bind the purchase, the store is not a party, and support is the developer's
          responsibility. Three sentences, because each one is a thing a reader asks. */}
      <Section
        titleKey="siteTerms.store.title"
        bodyKeys={[
          'siteTerms.store.body',
          'siteTerms.store.notParty',
          'siteTerms.store.support',
        ]}
      />

      <Section
        titleKey="siteTerms.sharing.title"
        bodyKeys={[
          'siteTerms.sharing.link',
          'siteTerms.sharing.responsible',
          'siteTerms.sharing.revoke',
          'siteTerms.sharing.misuse',
        ]}
      />

      <Section
        titleKey="siteTerms.ai.title"
        bodyKeys={['siteTerms.ai.optional', 'siteTerms.ai.wrong', 'siteTerms.ai.yours']}
      />

      <Section
        titleKey="siteTerms.use.title"
        bodyKeys={['siteTerms.use.lawful', 'siteTerms.use.service', 'siteTerms.use.resell']}
      />

      <Section
        titleKey="siteTerms.availability.title"
        bodyKeys={[
          'siteTerms.availability.offline',
          'siteTerms.availability.server',
          'siteTerms.availability.change',
        ]}
      />

      <Section
        titleKey="siteTerms.noWarranty.title"
        bodyKeys={[
          'siteTerms.noWarranty.asIs',
          'siteTerms.noWarranty.liability',
          'siteTerms.noWarranty.statutory',
          'siteTerms.noWarranty.backup',
        ]}
      />

      <Section
        titleKey="siteTerms.ending.title"
        bodyKeys={[
          'siteTerms.ending.you',
          'siteTerms.ending.uploaded',
          'siteTerms.ending.provider',
        ]}
      />

      {/* RULINGS R133: England and Wales, and its courts. No arbitration clause and no
          class-action waiver beside it — RULINGS R139 confirms that absence as a
          decision. Do not add either. */}
      <Section titleKey="siteTerms.law.title" bodyKeys={['siteTerms.law.body']} />

      <Section
        titleKey="siteTerms.changes.title"
        bodyKeys={['siteTerms.changes.body', 'siteTerms.changes.continue']}
      />
    </PageFrame>
  );
}
